//* 지망생 다이어리 상세화면
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useParams, useNavigate, Link } from "react-router-dom";
import { getLanguageValue } from "locales/i18n";
import { PAGE_TYPE } from "enums";
import { getImageUrl, getProfileImageUrlByAccountTypeCode } from "helpers/Image.helper";
import { getVideoUrl } from "helpers/Video.helper";
import { formatTimeAgo } from "helpers/Datetime.helper";
import { IDiary } from "api/interfaces/profile.interface";
import { getDiaryDetail, toggleDiaryLike, deleteDiary, toggleDiaryHidden } from "api/student/v2/diary.api";
import VideoPlayer from "commons/video/VideoPlayer";
import CommentSection from "./CommentSection";
import { usePopup } from "contexts/PopupContext";
import LikePopupForDiary from "pages/student/v2/popup/LikePopupForDiary";
import DiaryStatsPopup from "../../popup/DiaryStatsPopup";
import PostImagePopupContainer from "../../popup/PostImagePopup.container";

const DiaryDetail: React.FC = () => {
    const { diaryId } = useParams<{ diaryId: string }>();
    const navigate = useNavigate();
    const { info: userInfo } = useSelector((state: any) => state.user);
    const { showPopup } = usePopup();

    const [diary, setDiary] = useState<IDiary | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activeKebab, setActiveKebab] = useState<boolean>(false);
    const [isHidden, setIsHidden] = useState<boolean>(false);

    useEffect(() => {
        if (diaryId) {
            fetchDiaryData();
        }
    }, [diaryId]);

    const fetchDiaryData = async () => {
        if (!diaryId) return;

        try {
            setIsLoading(true);
            setError(null);
            const result = await getDiaryDetail(diaryId);
            setDiary(result.diary);
            setIsHidden(result.diary.isHidden);
        } catch (error) {
            console.error("다이어리 상세 조회 실패:", error);
            setError(getLanguageValue(PAGE_TYPE.STUDENT, "diaryDetail.txtLoadError"));
        } finally {
            setIsLoading(false);
        }
    };

    const handleDiaryLikeToggle = async () => {
        if (!diary) return;

        try {
            console.log("좋아요 토글 전:", { isLiked: diary.isLiked, likeCount: diary.likeCount });
            const result = await toggleDiaryLike(diary.id.toString());
            console.log("API 응답:", result);

            setDiary(prev => prev ? {
                ...prev,
                isLiked: result.isLiked,
                likeCount: result.likeCount
            } : null);
        } catch (error) {
            console.error("좋아요 토글 실패:", error);
        }
    };

    // 좋아요 목록 팝업 표시 함수
    const handleShowLikePopup = () => {
        if (diary && diary.likeCount && diary.likeCount > 0) {
            const popupContent = (
                <LikePopupForDiary
                    diaryId={diaryId!}
                    onClose={() => showPopup(null)}
                />
            );
            showPopup(popupContent);
        }
    };

    // 통계 팝업 표시 함수
    const handleShowStatsPopup = () => {
        const popupContent = (
            <DiaryStatsPopup
                diaryId={diaryId!}
                onClose={() => showPopup(null)}
            />
        );
        showPopup(popupContent);
    };

    // 이미지 팝업 표시 함수
    const handleShowImagePopup = (index: number) => {
        if (diary?.diaryImages) {
            const imageInfoList = diary.diaryImages.map(diaryImage => diaryImage.image);
            const popupContent = (
                <PostImagePopupContainer
                    imageInfoList={imageInfoList}
                    initialSlideIdx={index}
                    onClose={() => showPopup(null)}
                />
            );
            showPopup(popupContent);
        }
    };

    // 다이어리 삭제 함수
    const handleDeleteDiary = async () => {
        if (!diary) return;

        // 내가 작성한 다이어리인지 확인
        if (diary.userId !== userInfo?.id) {
            alert(getLanguageValue(PAGE_TYPE.STUDENT, "diaryDetail.txtCannotDeleteOthersDiary"));
            return;
        }

        // 확인 다이얼로그
        const isConfirmed = window.confirm(getLanguageValue(PAGE_TYPE.STUDENT, "diaryDetail.txtConfirmDelete"));

        if (isConfirmed) {
            try {
                // 다이어리 삭제 API 호출
                await deleteDiary(diaryId!);

                // 성공적으로 삭제된 경우 목록으로 이동
                navigate("/student/growth/diary");
            } catch (error) {
                console.error("다이어리 삭제 실패:", error);
                alert(getLanguageValue(PAGE_TYPE.STUDENT, "diaryDetail.txtDeleteError"));
            }
        }
    };

    // 다이어리 감추기 토글 함수
    const handleToggleDiaryHidden = async () => {
        if (!diaryId) return;

        try {
            const result = await toggleDiaryHidden(diaryId);
            setIsHidden(result.isHidden);

            // 성공 메시지 표시 (선택사항)
            console.log(result.message);

            // 다이어리 데이터 업데이트 (isHidden 상태 반영)
            setDiary(prev => prev ? {
                ...prev,
                isHidden: result.isHidden
            } : null);

        } catch (error) {
            console.error("다이어리 감추기 토글 실패:", error);
            alert(getLanguageValue(PAGE_TYPE.STUDENT, "diaryDetail.txtHideToggleError"));
        }
    };

    // 케밥 메뉴 외부 클릭 시 닫기
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (activeKebab) {
                setActiveKebab(false);
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [activeKebab]);

    if (isLoading) {
        return (
            <main className="bg-gray-50 min-h-screen">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    {/* 뒤로가기 버튼 스켈레톤 */}
                    <div className="animate-pulse mb-6">
                        <div className="h-6 bg-gray-300 rounded w-32"></div>
                    </div>

                    {/* 다이어리 상세 정보 스켈레톤 */}
                    <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                        <div className="p-4">
                            {/* 헤더 스켈레톤 */}
                            <div className="flex items-center space-x-2 mb-4">
                                <div className="w-8 h-8 bg-gray-300 rounded-full"></div>
                                <div>
                                    <div className="h-3 bg-gray-300 rounded w-20 mb-1"></div>
                                    <div className="h-2 bg-gray-300 rounded w-16"></div>
                                </div>
                            </div>

                            {/* 제목과 포트폴리오 여부 스켈레톤 */}
                            <div className="mb-4">
                                <div className="flex items-center justify-between mb-1">
                                    <div className="h-5 bg-gray-300 rounded w-3/4"></div>
                                    <div className="h-4 bg-gray-300 rounded w-16"></div>
                                </div>
                            </div>

                            {/* 내용 스켈레톤 */}
                            <div className="mb-4 space-y-2">
                                <div className="h-3 bg-gray-300 rounded w-full"></div>
                                <div className="h-3 bg-gray-300 rounded w-5/6"></div>
                                <div className="h-3 bg-gray-300 rounded w-4/6"></div>
                            </div>

                            {/* 비디오 스켈레톤 */}
                            <div className="mb-4">
                                <div className="h-32 bg-gray-300 rounded-lg"></div>
                            </div>

                            {/* 이미지 스켈레톤 */}
                            <div className="mb-4">
                                <div className="flex gap-2">
                                    <div className="w-24 h-24 bg-gray-300 rounded-lg flex-shrink-0"></div>
                                    <div className="w-24 h-24 bg-gray-300 rounded-lg flex-shrink-0"></div>
                                    <div className="w-24 h-24 bg-gray-300 rounded-lg flex-shrink-0"></div>
                                </div>
                            </div>

                            {/* 통계 정보 스켈레톤 */}
                            <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                                <div className="flex items-center space-x-3">
                                    <div className="h-3 bg-gray-300 rounded w-6"></div>
                                    <div className="h-3 bg-gray-300 rounded w-6"></div>
                                </div>
                                <div className="h-3 bg-gray-300 rounded w-4"></div>
                            </div>
                        </div>
                    </div>

                    {/* 댓글 섹션 스켈레톤 */}
                    <div className="mt-4 bg-white rounded-2xl shadow-sm p-4">
                        <div className="h-4 bg-gray-300 rounded w-12 mb-3"></div>
                        <div className="space-y-3">
                            {[1, 2, 3].map((index) => (
                                <div key={index} className="border border-gray-200 rounded-lg p-2 animate-pulse">
                                    <div className="flex items-center space-x-2 mb-1.5">
                                        <div className="w-5 h-5 bg-gray-300 rounded-full"></div>
                                        <div className="space-y-1">
                                            <div className="h-3 bg-gray-300 rounded w-20"></div>
                                            <div className="h-2 bg-gray-300 rounded w-12"></div>
                                        </div>
                                    </div>
                                    <div className="space-y-1.5 mb-1.5">
                                        <div className="h-3 bg-gray-300 rounded w-full"></div>
                                        <div className="h-3 bg-gray-300 rounded w-3/4"></div>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center space-x-2">
                                            <div className="w-3 h-3 bg-gray-300 rounded"></div>
                                            <div className="h-2 bg-gray-300 rounded w-6"></div>
                                        </div>
                                        <div className="h-2 bg-gray-300 rounded w-12"></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </main>
        );
    }

    if (error || !diary) {
        return (
            <main className="bg-gray-50 min-h-screen">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    <div className="text-center py-12">
                        <div className="text-red-500 text-lg mb-4">
                            {error || getLanguageValue(PAGE_TYPE.STUDENT, "diaryDetail.txtCannotLoadDiary")}
                        </div>
                        <Link
                            to="/student/growth/diary"
                            className="inline-flex items-center px-4 py-2 bg-[#CF3688] text-white rounded-lg hover:bg-[#CF3688]/90 transition-colors"
                        >
                            {getLanguageValue(PAGE_TYPE.STUDENT, "diaryDetail.txtBackToList")}
                        </Link>
                    </div>
                </div>
            </main>
        );
    }

    return (
        <main className="bg-gray-50 min-h-screen">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
                {/* 헤더 */}
                <div className="mb-8">
                    <div className="mb-4">
                        <button
                            onClick={() => navigate("/student/growth/diary")}
                            className="p-2 text-gray-600 hover:text-gray-900 transition-colors"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                    </div>
                    <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                        {diary.title}
                    </h1>
                </div>

                {/* 다이어리 상세 정보 */}
                <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                    <div className="p-4">
                        {/* 헤더 */}
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center space-x-2">
                                <div className="w-8 h-8 rounded-full overflow-hidden">
                                    <img
                                        src={getImageUrl(diary.user.userStudent.profileImage) || "/imgs/dummy/basic-img.png"}
                                        alt={diary.user.userStudent.nickname}
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-gray-900">{diary.user.userStudent.nickname}</p>
                                    <p className="text-xs text-gray-500">{formatTimeAgo(diary.createdAt)}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                {diary.isPortfolio && (
                                    <div className="flex items-center gap-1 px-2 py-0.5 bg-[#CF3688]/10 text-[#CF3688] rounded-full text-xs font-medium">
                                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        {getLanguageValue(PAGE_TYPE.STUDENT, "diaryDetail.txtPortfolio")}
                                    </div>
                                )}

                                {/* 숨겨진 상태 표시 아이콘 */}
                                {isHidden && (
                                    <div className="flex items-center gap-1 px-2 py-0.5 bg-orange-100 text-orange-600 rounded-full text-xs font-medium">
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                                        </svg>
                                        {getLanguageValue(PAGE_TYPE.STUDENT, "diaryDetail.txtHidden")}
                                    </div>
                                )}

                                {/* 케밥 버튼 - 내가 작성한 다이어리인 경우에만 표시 */}
                                {diary.userId === userInfo?.id && (
                                    <div className="relative">
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setActiveKebab(!activeKebab);
                                            }}
                                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-300"
                                            aria-label="다이어리 메뉴"
                                        >
                                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                                <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
                                            </svg>
                                        </button>

                                        {/* 케밥 툴팁 */}
                                        {activeKebab && (
                                            <div className="absolute right-0 top-full mt-2 bg-white border border-gray-200 rounded-lg shadow-xl z-20 min-w-[140px] py-1">
                                                <button
                                                    className={`w-full px-4 py-3 text-left text-sm hover:bg-gray-50 transition-colors duration-200 ${isHidden ? 'text-orange-600' : 'text-gray-700'
                                                        }`}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleToggleDiaryHidden();
                                                        setActiveKebab(false);
                                                    }}
                                                >
                                                    <span className="font-medium">
                                                        {isHidden
                                                            ? getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.diary.btnShow")
                                                            : getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.diary.btnHide")
                                                        }
                                                    </span>
                                                </button>
                                                <button
                                                    className="w-full px-4 py-3 text-left text-sm text-red-600 hover:bg-red-50 transition-colors duration-200"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteDiary();
                                                        setActiveKebab(false);
                                                    }}
                                                >
                                                    <span className="font-medium">{getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.diary.btnDelete")}</span>
                                                </button>
                                                <hr className="my-1 border-gray-100" />
                                                <button
                                                    className="w-full px-4 py-3 text-left text-sm text-gray-500 hover:bg-gray-50 transition-colors duration-200"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setActiveKebab(false);
                                                    }}
                                                >
                                                    <span className="font-medium">{getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.diary.btnClose")}</span>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>


                        {/* 내용 */}
                        <div className="mb-4">
                            <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{diary.content}</p>
                        </div>

                        {/* 비디오 (우선 출력) */}
                        {diary.diaryVideos && diary.diaryVideos.length > 0 && (
                            <div className="mb-4">
                                <div className="space-y-3">
                                    {diary.diaryVideos.map((video, index) => (
                                        <div key={index} className="rounded-lg overflow-hidden">
                                            <VideoPlayer
                                                video={video.video}
                                                className="w-full"
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* 이미지 */}
                        {diary.diaryImages && diary.diaryImages.length > 0 && (
                            <div className="mb-4">
                                <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                                    {diary.diaryImages.map((image, index) => (
                                        <div key={index} className="flex-shrink-0 aspect-square w-24 rounded-lg overflow-hidden">
                                            <img
                                                src={getImageUrl(image.image)}
                                                alt={`${getLanguageValue(PAGE_TYPE.STUDENT, "diaryDetail.txtDiaryImageAlt")} ${index + 1}`}
                                                className="w-full h-full object-cover hover:opacity-80 transition-opacity cursor-pointer"
                                                onClick={() => handleShowImagePopup(index)}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* 좋아요 및 댓글 수 */}
                        <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                            <div className="flex items-center space-x-3">
                                <button
                                    onClick={handleDiaryLikeToggle}
                                    className={`flex items-center space-x-1 transition-opacity hover:opacity-80 ${diary.isLiked ? 'text-[#CF3688]' : 'text-gray-500'
                                        }`}
                                >
                                    <svg
                                        className={`w-4 h-4 ${diary.isLiked ? 'text-[#CF3688]' : 'text-gray-500'}`}
                                        fill={diary.isLiked ? '#CF3688' : 'none'}
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                                    </svg>
                                    <span className="text-xs font-medium">{diary.likeCount || 0}</span>
                                </button>
                                <div className="flex items-center space-x-1 text-gray-500">
                                    <img src="/imgs/icons/icon-comment.svg" alt="댓글" className="w-4 h-4" />
                                    <span className="text-xs font-medium">{diary.commentCount || 0}</span>
                                </div>
                                <div className="flex items-center space-x-1 text-gray-500">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                                    </svg>
                                    <span className="text-xs font-medium">{(diary as any).bookmarkCount || 0}</span>
                                </div>
                            </div>

                            {/* 분석 아이콘 */}
                            <button
                                onClick={handleShowStatsPopup}
                                className="text-gray-500 hover:text-gray-700 transition-colors"
                            >
                                <img src="/imgs/icons/icon-analyze-active.svg" alt="분석" className="w-4 h-4" />
                            </button>
                        </div>

                        {/* 활동영역 - 좋아요한 유저들 */}
                        {diary.likes && diary.likes.length > 0 && (
                            <div className="pt-3 border-t border-gray-100">
                                <div
                                    className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 py-1.5 rounded-lg transition-colors"
                                    onClick={handleShowLikePopup}
                                >
                                    {/* 좋아요한 유저들의 프로필 사진 */}
                                    <div className="flex -space-x-2">
                                        {diary.likes.slice(0, 3).map((like, index) => (
                                            <div
                                                key={like.userId}
                                                className="w-6 h-6 rounded-full border-2 border-white overflow-hidden shadow-sm"
                                                style={{ zIndex: 5 - index }}
                                            >
                                                <img
                                                    src={getProfileImageUrlByAccountTypeCode(like?.user, 32, 32) || "/imgs/dummy/basic-img.png"}
                                                    alt={like?.user?.userStudent?.nickname || like?.user?.username || "User"}
                                                    className="w-full h-full object-cover"
                                                    onError={(e) => {
                                                        const target = e.target as HTMLImageElement;
                                                        target.src = "/imgs/dummy/basic-img.png";
                                                    }}
                                                />
                                            </div>
                                        ))}
                                    </div>

                                    {/* 좋아요 텍스트 - 우선순위별 조건 */}
                                    {diary.castingManagerLikeCount !== undefined && diary.castingManagerLikeCount > 0 ? (
                                        /* 1순위: 캐스팅 매니저 좋아요가 있는 경우 */
                                        <div className="flex items-center space-x-1 text-xs text-gray-600">
                                            <span className="text-gray-500">
                                                {diary.castingManagerLikeCount === 1 
                                                    ? getLanguageValue(PAGE_TYPE.STUDENT, "diaryDetail.txtCastingManagerLikeSingle")
                                                    : getLanguageValue(PAGE_TYPE.STUDENT, "diaryDetail.txtCastingManagerLikeMultiple")
                                                }
                                            </span>
                                        </div>
                                    ) : (
                                        /* 2순위: 캐스팅 매니저 좋아요가 없고, 일반 좋아요가 3개 초과인 경우 */
                                        diary.likeCount > 3 && (
                                            <div className="flex items-center space-x-1 text-xs text-gray-600">
                                                <span className="text-gray-500">
                                                    {getLanguageValue(PAGE_TYPE.STUDENT, "diaryDetail.txtOthersLike")}
                                                </span>
                                            </div>
                                        )
                                    )}

                                    {/* 화살표 아이콘 */}
                                    <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                    </svg>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* 댓글 섹션 */}
                <CommentSection diaryId={diaryId!} diaryAuthorId={diary.user.id} />
            </div>
        </main>
    );
};

export default DiaryDetail;
